import React, { useContext } from 'react';
import { Container, Row, Col, Carousel, Card, Image, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './HomePage.css';
import { UserContext } from '../Contexts/UserContext';
import { Helmet } from 'react-helmet';

const HomePage = () => {
  const { user } = useContext(UserContext);
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>DNACloudHub | Home</title>
        <meta name="description" content="Welcome to DNACloudHub, the leading cloud-based platform offering a seamless, user-friendly service that hosts the infrastructure for running qpAdm models." />
        <meta property="og:title" content={'DNACloudHub | Home'} />
        <meta property="og:description" content={'"Welcome to DNACloudHub, the leading cloud-based platform offering a seamless, user-friendly service that hosts the infrastructure for running qpAdm models."'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dnacloudhub.com/`} />
      </Helmet>
      <Col md={11} className="home-page mx-auto">
        {/* Hero Section */}
        <motion.header
          className="hero-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <Carousel fade className="my-5">
            <Carousel.Item>
              <div className="hero">
                <img
                  className="d-block w-100"
                  src="./main1.jpeg"
                  alt="First Slide"
                  height="600px"
                  style={{ objectFit: 'cover' }}
                />
                <Carousel.Caption className="carousel-caption">
                  <motion.div {...fadeInUp}>
                    <h1 className="display-4 fw-bold">Revolutionary Web-Based DNACloudHub</h1>
                    <p className="lead">
                      Analyze ancient DNA effortlessly with our powerful and intuitive web platform.
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      as={Link}
                      to={user && user.uuid ? "/run_qpadm" : "/signup"}
                      className="shadow-lg"
                    >
                      Get Started
                    </Button>
                  </motion.div>
                </Carousel.Caption>
              </div>
            </Carousel.Item>

            <Carousel.Item>
              <div className="hero">
                <img
                  className="d-block w-100"
                  src="./main5.jpeg"
                  alt="Second Slide"
                  height="600px"
                  style={{ objectFit: 'cover' }}
                />
                <Carousel.Caption className="carousel-caption">
                  <motion.div {...fadeInUp}>
                    <h1 className="display-4 fw-bold">Discover Ancestry Proportions</h1>
                    <p className="lead">
                      Leverage advanced statistical models to estimate ancestry proportions from ancient populations.
                    </p>
                  </motion.div>
                </Carousel.Caption>
              </div>
            </Carousel.Item>

            <Carousel.Item>
              <div className="hero">
                <img
                  className="d-block w-100"
                  src="./main4.jpg"
                  alt="Third Slide"
                  height="600px"
                  style={{ objectFit: 'cover' }}
                />
                <Carousel.Caption className="carousel-caption">
                  <motion.div {...fadeInUp}>
                    <h1 className="display-4 fw-bold">Streamlined Interface for Analysis</h1>
                    <p className="lead">
                      An intuitive interface that simplifies complex genetic analysis, making research accessible to everyone.
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      as={Link}
                      to="/services"
                      className="shadow-lg"
                    >
                      Learn More
                    </Button>
                  </motion.div>
                </Carousel.Caption>
              </div>
            </Carousel.Item>
          </Carousel>
        </motion.header>

        {/* About Section */}
        <Container className="my-5">
          <Row>
            {['What is DNACloudHub?', 'How accurate is DNACloudHub?', 'Who uses DNACloudHub?'].map((title, index) => (
              <Col md={4} className="mb-4" key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <Card className="feature-card">
                    <Card.Body className="text-center p-4">
                      <img
                        className="rounded-circle mb-4"
                        src={`./main${index + 2}.jpeg`}
                        alt={title}
                        style={{ width: '120px', height: '120px', margin: '1rem auto' }}
                      />
                      <h2 className="section-subheading">{title}</h2>
                      <Card.Text>
                        {index === 0 && "DNACloudHub is a cloud-based DNA platform offering a service that hosts the infrastructure for running qpAdm. It enables researchers to effortlessly analyze genetic data by running powerful models like qpAdm in a scalable, user-friendly environment."}
                        {index === 1 && "DNACloudHub is known for its reliability in delivering accurate results, utilizing robust statistical tools like qpAdm for estimating genetic ancestry proportions. Researchers can trust our platform to produce precise insights backed by cutting-edge models and infrastructure."}
                        {index === 2 && "DNACloudHub is used by geneticists, researchers, and scientists in fields such as anthropology, archaeology, and evolutionary biology. It provides essential tools for analyzing genetic relationships, migrations, and population histories through advanced models like qpAdm."}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>

        <hr className="divider" />

        {/* Sign-Up Section */}
        <Container className="mb-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Row className="align-items-center">
              <Col md={7}>
                <div className="pe-md-5">
                  <h2 className="section-heading">
                    Sign Up for Your Free Account
                  </h2>
                  <p>
                    Begin your journey by registering for a free account to unlock access to our comprehensive platform tailored for genetic analysis and research in population dynamics. With your account, you can run models and store results effortlessly.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    as={Link}
                    to={user && user.uuid ? "/run_qpadm" : "/signup"}
                    className="shadow"
                  >
                    Sign Up Now
                  </Button>
                </div>
              </Col>
              <Col md={5}>
                <Image
                  src="./main10.jpeg"
                  alt="Sign Up"
                  fluid
                  className="rounded-3 shadow"
                  style={{ height: '500px', objectFit: 'cover' }}
                />
              </Col>
            </Row>
          </motion.div>
        </Container>

        <hr className="divider" />

        {/* Instantly Run Models Section */}
        <Container className="mb-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Row className="align-items-center">
              <Col md={7} className="order-md-2">
                <div className="ps-md-5 text-md-end">
                  <h2 className="section-heading">
                    Instantly Run Your Custom Models in the Cloud
                  </h2>
                  <p>
                    Experience seamless execution of your custom models in the cloud, allowing you to analyze data and view results instantly. Streamline your workflow and accelerate discoveries with our efficient platform designed for fast, reliable performance in research and data analysis.
                  </p>
                  <Button
                    variant="secondary"
                    size="lg"
                    as={Link}
                    to="/services"
                    className="shadow"
                  >
                    Start Now
                  </Button>
                </div>
              </Col>
              <Col md={5} className="order-md-1">
                <Image
                  src="./main11.jpeg"
                  alt="Cloud models"
                  fluid
                  className="rounded-3 shadow"
                  style={{ height: '500px', objectFit: 'cover' }}
                />
              </Col>
            </Row>
          </motion.div>
        </Container>

        <hr className="divider" />

        {/* Features Section */}
        <Container className="my-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-heading text-center mb-4">
              Why Choose Our Platform?
            </h2>
            <Row>
              {[
                {
                  title: 'Cloud-Based Execution',
                  description: 'Run your custom models instantly in the cloud for fast and reliable results, hosted on our powerful infrastructure.',
                  link: true
                },
                {
                  title: 'Powerful Analytics',
                  description: 'Access cutting-edge tools for genetic analysis and gain deep insights into population dynamics.'
                },
                {
                  title: 'Data Security',
                  description: 'We prioritize your data security with robust encryption and privacy measures.'
                }
              ].map((feature, index) => (
                <Col md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <Card className="feature-card">
                      <Card.Body className="text-center">
                        <h5 className="section-subheading">{feature.title}</h5>
                        <p>{feature.description}</p>
                        {feature.link && (
                          <Button
                            variant="outline-primary"
                            as={Link}
                            to="/services"
                          >
                            Learn More
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>

        {/* Final Call-to-Action Section */}
        <motion.section
          className="cta-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Container className="text-center">
            <h2 className="section-heading">Start Your Research Journey Today!</h2>
            <p className="mx-auto mb-4" style={{ maxWidth: '700px' }}>
              Sign up for free and gain full access to our platform's powerful genetic analysis tools and datasets.
            </p>
            <Button
              variant="primary"
              size="lg"
              as={Link}
              to={user && user.uuid ? "/run_qpadm" : "/signup"}
              className="shadow-lg"
            >
              Sign Up Now
            </Button>
          </Container>
        </motion.section>
      </Col>
    </>
  );
};

export default HomePage;
